import React, { useEffect, useRef, useState } from "react";
import { TagEditor } from "../filmdb/FilmDbTags";
import { toast } from "sonner";
import { Loader2, Trash2, Upload, Link2, X, Plus, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { api } from "../../lib/api";

const inputCls = "w-full bg-[#101010] border border-white/15 text-white text-sm px-3 py-2.5 focus:outline-none focus:border-[var(--org-accent)]";
const labelCls = "block text-[9px] uppercase tracking-[0.3em] text-[#a0a0a0] font-bold mb-1.5";

export function Field({ label, children }) {
  return <label className="block"><span className={labelCls}>{label}</span>{children}</label>;
}

export function EditorShell({ open, onClose, title, eyebrow, children, onSave, saving, onDelete, deleteLabel, testId, saveLabel = "Save" }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl w-[95vw] max-h-[92vh] overflow-y-auto bg-[#141414] border border-white/10 p-0 [&>button]:hidden" data-testid={testId}>
        <div className="flex items-start justify-between gap-3 px-6 pt-5 pb-3 border-b border-white/10">
          <div>
            <div className="text-[9px] uppercase tracking-[0.35em] text-[var(--org-accent)] font-bold">{eyebrow}</div>
            <DialogTitle className="font-heading text-2xl uppercase tracking-tight text-white mt-1">{title}</DialogTitle>
          </div>
          <button onClick={onClose} className="p-2 text-[#a0a0a0] hover:text-white" data-testid={`${testId}-close`}><X className="w-4 h-4" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">{children}</div>
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-white/10 bg-[#101010]">
          {onDelete ? (
            <button onClick={onDelete} disabled={saving} className="flex items-center gap-1.5 px-3 py-2 text-[10px] uppercase tracking-[0.25em] font-bold border border-red-400/40 text-red-300 hover:bg-red-400/10 disabled:opacity-50" data-testid={`${testId}-delete`}><Trash2 className="w-3.5 h-3.5" /> {deleteLabel || "Delete"}</button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-3 py-2 text-[10px] uppercase tracking-[0.25em] font-bold border border-white/20 text-[#a0a0a0] hover:text-white" data-testid={`${testId}-cancel`}>Cancel</button>
            <button onClick={onSave} disabled={saving} className="flex items-center gap-2 px-4 py-2 text-[10px] uppercase tracking-[0.25em] font-bold bg-[var(--org-accent)] text-[#121212] hover:bg-[var(--org-accent-hover)] disabled:opacity-50" data-testid={`${testId}-save`}>{saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {saveLabel}</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Option ----------
export function OptionEditor({ option, groupId, siblings = [], onClose, onSaved, onDeleted }) {
  const isNew = !option?.id;
  const [form, setForm] = useState({ name: option?.name || "", definition: option?.definition || "", cue: option?.cue || "", video_url: option?.video_url?.startsWith("/api/") ? "" : (option?.video_url || ""), parent_id: option?.parent_id || "", is_draft: !!option?.is_draft, extra_tags: option?.extra_tags || [] });
  const [uploaded, setUploaded] = useState(option?.video_url?.startsWith("/api/") ? option.video_url : "");
  const [attachment, setAttachment] = useState(option?.file_url ? { url: option.file_url, name: option.file_name, type: option.file_type } : null);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(null);
  const [fileProgress, setFileProgress] = useState(null);
  const fileRef = useRef(null);
  const docRef = useRef(null);
  const ensureOption = async () => {
    if (option?.id) return option.id;
    if (!form.name.trim()) throw new Error("Give the option a name first");
    const created = await api.tiqCreateOption({ group_id: groupId, ...form });
    option = created; onSaved(created, true);
    return created.id;
  };
  const uploadDoc = async (file) => {
    if (!file) return;
    setFileProgress(0);
    try {
      const id = await ensureOption();
      const saved = await api.tiqUploadFile(id, file, setFileProgress);
      setAttachment({ url: saved.file_url, name: saved.file_name, type: saved.file_type });
      onSaved(saved, true);
      toast.success(saved.file_type === "application/pdf" ? "PDF attached" : "Image attached");
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || "Upload failed"); } finally { setFileProgress(null); }
  };
  const clearDoc = async () => {
    try { const saved = await api.tiqClearFile(option.id); setAttachment(null); onSaved(saved, true); } catch { toast.error("Could not remove file"); }
  };
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const body = { ...form };
      if (uploaded && !form.video_url) delete body.video_url; // keep uploaded file
      const saved = isNew ? await api.tiqCreateOption({ group_id: groupId, ...body }) : await api.tiqUpdateOption(option.id, body);
      onSaved(saved);
      toast.success(isNew ? "Option added" : "Option saved");
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete "${option.name}"?`)) return;
    setSaving(true);
    try { await api.tiqDeleteOption(option.id); onDeleted(option.id); toast.success("Option deleted"); } catch { toast.error("Delete failed"); } finally { setSaving(false); }
  };
  const upload = async (file) => {
    if (!file) return;
    let id = option?.id;
    setProgress(0);
    try {
      if (!id) {
        if (!form.name.trim()) { setProgress(null); return toast.error("Give the option a name first"); }
        const created = await api.tiqCreateOption({ group_id: groupId, ...form });
        id = created.id; option = created; onSaved(created, true);
      }
      const saved = await api.tiqUploadVideo(id, file, setProgress);
      setUploaded(saved.video_url); setForm(f => ({ ...f, video_url: "" }));
      onSaved(saved, true);
      toast.success("Video uploaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); } finally { setProgress(null); }
  };
  const clearUpload = async () => {
    try { const saved = await api.tiqClearVideo(option.id); setUploaded(""); onSaved(saved, true); } catch { toast.error("Could not remove video"); }
  };

  return (
    <EditorShell open onClose={onClose} eyebrow={isNew ? "New option" : "Edit option"} title={form.name || "Option"} onSave={save} saving={saving} onDelete={isNew ? null : remove} deleteLabel="Delete option" testId="tiq-option-editor">
      <Field label="Term name"><input value={form.name} onChange={set("name")} className={inputCls} placeholder="e.g. Snake" data-testid="tiq-opt-name" /></Field>
      <Field label="Definition"><textarea value={form.definition} onChange={set("definition")} rows={4} className={`${inputCls} resize-y`} data-testid="tiq-opt-def" /></Field>
      <Field label="Coaching cue"><input value={form.cue} onChange={set("cue")} className={inputCls} placeholder="Short, punchy, sayable" data-testid="tiq-opt-cue" /></Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Parent option (indents this under it)">
          <select value={form.parent_id} onChange={set("parent_id")} className={inputCls} data-testid="tiq-opt-parent">
            <option value="">— None (top level) —</option>
            {siblings.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </Field>
        <div className="sm:col-span-2 border border-white/10 bg-[#101010]/60 p-4 space-y-2">
          <div className={labelCls}>Tags <span className="normal-case tracking-normal font-normal text-[#757575]">— Library › Section › Interaction, same names as the assessment</span></div>
          <TagEditor value={form.extra_tags} onChange={(v) => setForm(f => ({ ...f, extra_tags: v }))} testPrefix="tiq-opt-tags" />
        </div>
        <label className="flex items-center gap-3 border border-white/10 bg-[#101010]/60 px-4 py-3 mt-5 cursor-pointer">
          <input type="checkbox" checked={form.is_draft} onChange={(e) => setForm(f => ({ ...f, is_draft: e.target.checked }))} className="accent-[#f0606d] w-4 h-4" data-testid="tiq-opt-draft" />
          <span className="text-[12px] text-white">Draft <span className="text-[#757575]">— shows a Draft badge, hidden from the glossary</span></span>
        </label>
      </div>
      <div className="border border-white/10 p-4 space-y-3 bg-[#101010]/60">
        <div className={labelCls}>Film</div>
        <label className="flex items-center gap-2">
          <Link2 className="w-4 h-4 text-[#757575] shrink-0" />
          <input value={form.video_url} onChange={set("video_url")} className={inputCls} placeholder="Paste a YouTube, Vimeo, Loom or .mp4 link" data-testid="tiq-opt-video-url" />
        </label>
        <div className="flex items-center gap-3 flex-wrap">
          <input ref={fileRef} type="file" accept="video/mp4,video/quicktime,video/webm" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="tiq-opt-file" />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={progress !== null} className="flex items-center gap-2 px-3 py-2 text-[10px] uppercase tracking-[0.25em] font-bold border border-white/20 text-white hover:border-[var(--org-accent)] hover:text-[var(--org-accent)] disabled:opacity-50" data-testid="tiq-opt-upload-btn">
            {progress !== null ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} {progress !== null ? `Uploading ${progress}%` : "Upload MP4"}
          </button>
          {uploaded && !form.video_url && (
            <div className="flex items-center gap-2 text-[11px] text-emerald-300" data-testid="tiq-opt-uploaded">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Uploaded file attached
              <button type="button" onClick={clearUpload} className="text-[#a0a0a0] hover:text-red-400" title="Remove uploaded video" data-testid="tiq-opt-clear-video"><X className="w-3.5 h-3.5" /></button>
            </div>
          )}
          <span className="text-[10px] text-[#757575]">MP4 · up to 200 MB{uploaded && form.video_url ? " · the link above will replace the uploaded file" : ""}</span>
        </div>
        {progress !== null && <div className="h-1 bg-white/10"><div className="h-full bg-[var(--org-accent)]" style={{ width: `${progress}%`, transition: "width .2s" }} /></div>}
      </div>
      <div className="border border-white/10 p-4 space-y-3 bg-[#101010]/60">
        <div className={labelCls}>Image or PDF (shown under the film)</div>
        {attachment && (
          <div className="flex items-center gap-3 text-[12px]" data-testid="tiq-opt-attachment">
            {attachment.type !== "application/pdf" && <img src={`${process.env.REACT_APP_BACKEND_URL}${attachment.url}`} alt="" className="h-14 border border-white/10" />}
            <span className="flex items-center gap-1.5 text-white min-w-0"><FileText className="w-3.5 h-3.5 shrink-0 text-[#a0a0a0]" /><span className="truncate">{attachment.name || (attachment.type === "application/pdf" ? "Document.pdf" : "Image")}</span></span>
            <button type="button" onClick={clearDoc} className="text-[#a0a0a0] hover:text-red-400" title="Remove" data-testid="tiq-opt-clear-file"><X className="w-3.5 h-3.5" /></button>
          </div>
        )}
        <div className="flex items-center gap-3 flex-wrap">
          <input ref={docRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif,application/pdf" className="hidden" onChange={(e) => uploadDoc(e.target.files?.[0])} data-testid="tiq-opt-doc-file" />
          <button type="button" onClick={() => docRef.current?.click()} disabled={fileProgress !== null} className="flex items-center gap-2 px-3 py-2 text-[10px] uppercase tracking-[0.25em] font-bold border border-white/20 text-white hover:border-[var(--org-accent)] hover:text-[var(--org-accent)] disabled:opacity-50" data-testid="tiq-opt-doc-btn">
            {fileProgress !== null ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} {fileProgress !== null ? `Uploading ${fileProgress}%` : (attachment ? "Replace PNG / PDF" : "Upload PNG / PDF")}
          </button>
          <span className="text-[10px] text-[#757575]">PNG · JPG · WebP · PDF · up to 25 MB</span>
        </div>
        {fileProgress !== null && <div className="h-1 bg-white/10"><div className="h-full bg-[var(--org-accent)]" style={{ width: `${fileProgress}%`, transition: "width .2s" }} /></div>}
      </div>
    </EditorShell>
  );
}

// ---------- Group ----------
export function GroupEditor({ group, categoryId, categories = [], onClose, onSaved, onDeleted }) {
  const isNew = !group?.id;
  const [form, setForm] = useState({ title: group?.title || "", subtitle: group?.subtitle || "", category_id: group?.category_id || categoryId });
  const [saving, setSaving] = useState(false);
  const [image, setImage] = useState(group?.image_url || "");
  const [uploading, setUploading] = useState(false);
  const imgRef = useRef(null);
  const uploadImage = async (file) => {
    if (!file) return;
    let id = group?.id;
    setUploading(true);
    try {
      if (!id) {
        if (!form.title.trim()) { setUploading(false); return toast.error("Give the step a title first"); }
        const created = await api.tiqCreateGroup({ category_id: categoryId, ...form });
        id = created.id; group = created; onSaved(created, true);
      }
      const saved = await api.tiqUploadGroupImage(id, file);
      setImage(saved.image_url); onSaved(saved, true);
      toast.success("Image added");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); } finally { setUploading(false); }
  };
  const clearImage = async () => {
    try { const saved = await api.tiqClearGroupImage(group.id); setImage(""); onSaved(saved, true); } catch { toast.error("Could not remove image"); }
  };
  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    setSaving(true);
    try {
      const body = { ...form };
      if (isNew || body.category_id === group.category_id) delete body.category_id;
      onSaved(isNew ? await api.tiqCreateGroup({ category_id: categoryId, ...body }) : await api.tiqUpdateGroup(group.id, body));
      toast.success(isNew ? "Step added" : (body.category_id ? "Step moved" : "Step saved"));
    }
    catch { toast.error("Save failed"); } finally { setSaving(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete step "${group.title}" and all its options?`)) return;
    setSaving(true);
    try { await api.tiqDeleteGroup(group.id); onDeleted(group.id); } catch { toast.error("Delete failed"); } finally { setSaving(false); }
  };
  return (
    <EditorShell open onClose={onClose} eyebrow={isNew ? "New step" : "Edit step"} title={form.title || "Step"} onSave={save} saving={saving} onDelete={isNew ? null : remove} deleteLabel="Delete step" testId="tiq-group-editor">
      <Field label="Step title"><input value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} className={inputCls} placeholder="e.g. Move" data-testid="tiq-grp-title" /></Field>
      <Field label="Subtitle"><input value={form.subtitle} onChange={(e) => setForm(f => ({ ...f, subtitle: e.target.value }))} className={inputCls} placeholder="e.g. How to get out" data-testid="tiq-grp-sub" /></Field>
      {!isNew && categories.length > 1 && (
        <Field label="Category (move this step and all its options)">
          <select value={form.category_id} onChange={(e) => setForm(f => ({ ...f, category_id: e.target.value }))} className={inputCls} data-testid="tiq-grp-category">
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
      )}
      <div className="border border-white/10 p-4 space-y-3 bg-[#101010]/60">
        <div className={labelCls}>Section image (shown under the options)</div>
        {image && <img src={image.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${image}` : image} alt="Section diagram" className="max-h-48 border border-white/10" data-testid="tiq-grp-image-preview" />}
        <div className="flex items-center gap-3 flex-wrap">
          <input ref={imgRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])} data-testid="tiq-grp-image-file" />
          <button type="button" onClick={() => imgRef.current?.click()} disabled={uploading} className="flex items-center gap-2 px-3 py-2 text-[10px] uppercase tracking-[0.25em] font-bold border border-white/20 text-white hover:border-[var(--org-accent)] hover:text-[var(--org-accent)] disabled:opacity-50" data-testid="tiq-grp-image-btn">
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} {image ? "Replace image" : "Upload image"}
          </button>
          {image && <button type="button" onClick={clearImage} className="flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] hover:text-red-400" data-testid="tiq-grp-image-clear"><X className="w-3.5 h-3.5" /> Remove</button>}
          <span className="text-[10px] text-[#757575]">PNG / JPG / WebP · up to 10 MB</span>
        </div>
      </div>
    </EditorShell>
  );
}

// ---------- Category ----------
const SWATCHES = ["#a07cf0", "#5b8ff0", "#ff7a2e", "#34c28c", "#d8b854", "#f0606d", "#f472b6", "#22d3ee"];
export function CategoryEditor({ category, pageId, filmStudy = false, onClose, onSaved, onDeleted }) {
  const isNew = !category?.id;
  const [form, setForm] = useState({ name: category?.name || "", color: category?.color || SWATCHES[1], intro: category?.intro || "", role_text: category?.role_text || "", is_coverage: !!category?.is_coverage, defense: category?.defense || "", gives_up: category?.gives_up || "", ...(category?.is_film_study || filmStudy ? { is_film_study: true } : {}) });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try { onSaved(isNew ? await api.tiqCreateCategory({ page_id: pageId, ...form }) : await api.tiqUpdateCategory(category.id, form)); toast.success(isNew ? "Category added" : "Category saved"); }
    catch { toast.error("Save failed"); } finally { setSaving(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete "${category.name}" with all its steps and options?`)) return;
    setSaving(true);
    try { await api.tiqDeleteCategory(category.id); onDeleted(category.id); } catch { toast.error("Delete failed"); } finally { setSaving(false); }
  };
  return (
    <EditorShell open onClose={onClose} eyebrow={isNew ? "New category / role" : "Edit category"} title={form.name || "Category"} onSave={save} saving={saving} onDelete={isNew ? null : remove} deleteLabel="Delete category" testId="tiq-category-editor">
      <Field label="Name (tab label)"><input value={form.name} onChange={set("name")} className={inputCls} placeholder="e.g. Screener" data-testid="tiq-cat-name" /></Field>
      <div>
        <div className={labelCls}>Color</div>
        <div className="flex items-center gap-2 flex-wrap">
          {SWATCHES.map(c => <button key={c} type="button" onClick={() => setForm(f => ({ ...f, color: c }))} className={`w-7 h-7 rounded-full border-2 ${form.color === c ? "border-white" : "border-transparent"}`} style={{ background: c }} data-testid={`tiq-cat-color-${c.slice(1)}`} />)}
          <input value={form.color} onChange={set("color")} className={`${inputCls} !w-28`} data-testid="tiq-cat-color" />
        </div>
      </div>
      <Field label="Intro (shown above the steps)"><textarea value={form.intro} onChange={set("intro")} rows={3} className={`${inputCls} resize-y`} data-testid="tiq-cat-intro" /></Field>
      <Field label="Role card text (leave blank to hide this category from the Roles section)"><textarea value={form.role_text} onChange={set("role_text")} rows={3} className={`${inputCls} resize-y`} data-testid="tiq-cat-role" /></Field>
      <div className="border border-white/10 bg-[#101010]/60 p-4 space-y-3">
        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" checked={form.is_coverage} onChange={(e) => setForm(f => ({ ...f, is_coverage: e.target.checked }))} className="accent-[#f0606d] w-4 h-4" data-testid="tiq-cat-coverage" />
          <span className="text-[12px] text-white">This category is a <span className="font-bold">defensive coverage</span> <span className="text-[#757575]">— appears in the Coverage Cheat Sheet</span></span>
        </label>
        {form.is_coverage && (
          <>
            <Field label="Defense (what the defense does)"><textarea value={form.defense} onChange={set("defense")} rows={2} className={`${inputCls} resize-y`} data-testid="tiq-cat-defense" /></Field>
            <Field label="Gives up (what it leaves open)"><textarea value={form.gives_up} onChange={set("gives_up")} rows={2} className={`${inputCls} resize-y`} data-testid="tiq-cat-gives" /></Field>
          </>
        )}
      </div>
    </EditorShell>
  );
}

// ---------- Page ----------
export function PageEditor({ page, section = "tactical", sectionTitle = "Tactical IQ", onClose, onSaved, onDeleted }) {
  const isNew = !page?.id;
  const [form, setForm] = useState({
    title: page?.title || "", slug: page?.slug || "", eyebrow: page?.eyebrow || sectionTitle, tactic: page?.tactic || "",
    keys: page?.keys?.length ? page.keys : [{ n: "01", title: "", text: "" }, { n: "02", title: "", text: "" }],
    roles_title: page?.roles_title || "The Roles", roles_sub: page?.roles_sub || "Know your job before you learn your options.",
    menu_title: page?.menu_title || "",
    menu_sub: page?.menu_sub || "Pick your role, then work left to right. Tap any option for the definition and film.",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const setKey = (i, k, v) => setForm(f => ({ ...f, keys: f.keys.map((x, j) => j === i ? { ...x, [k]: v } : x) }));
  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    setSaving(true);
    try {
      const body = { ...form, keys: form.keys.filter(k => k.title || k.text).map((k, i) => ({ ...k, n: String(i + 1).padStart(2, "0") })) };
      if (!body.slug) delete body.slug;
      if (isNew) body.section = section;
      onSaved(isNew ? await api.tiqCreatePage(body) : await api.tiqUpdatePage(page.id, body));
      toast.success(isNew ? "Page created" : "Page saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete the "${page.title}" page and everything in it?`)) return;
    setSaving(true);
    try { await api.tiqDeletePage(page.id); onDeleted(page.id); } catch { toast.error("Delete failed"); } finally { setSaving(false); }
  };
  return (
    <EditorShell open onClose={onClose} eyebrow={isNew ? `New ${sectionTitle} page` : "Edit page"} title={form.title || "Page"} onSave={save} saving={saving} onDelete={isNew ? null : remove} deleteLabel="Delete page" testId="tiq-page-editor">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Title"><input value={form.title} onChange={set("title")} className={inputCls} placeholder="e.g. Off-Ball Screens" data-testid="tiq-page-title" /></Field>
        <Field label="URL slug"><input value={form.slug} onChange={set("slug")} className={inputCls} placeholder="auto from title" data-testid="tiq-page-slug" /></Field>
      </div>
      <Field label="Eyebrow"><input value={form.eyebrow} onChange={set("eyebrow")} className={inputCls} data-testid="tiq-page-eyebrow" /></Field>
      <Field label="The tactic (one sentence)"><textarea value={form.tactic} onChange={set("tactic")} rows={2} className={`${inputCls} resize-y`} data-testid="tiq-page-tactic" /></Field>
      <div>
        <div className={labelCls}>Key cards</div>
        <div className="space-y-2">
          {form.keys.map((k, i) => (
            <div key={i} className="grid grid-cols-[auto_1fr_auto] gap-2 items-start">
              <div className="font-heading text-xl text-[var(--org-accent)] pt-2 w-8">{String(i + 1).padStart(2, "0")}</div>
              <div className="space-y-1.5">
                <input value={k.title} onChange={(e) => setKey(i, "title", e.target.value)} className={inputCls} placeholder="Key title" data-testid={`tiq-page-key-title-${i}`} />
                <input value={k.text} onChange={(e) => setKey(i, "text", e.target.value)} className={inputCls} placeholder="Key description" data-testid={`tiq-page-key-text-${i}`} />
              </div>
              <button type="button" onClick={() => setForm(f => ({ ...f, keys: f.keys.filter((_, j) => j !== i) }))} className="p-2 text-[#757575] hover:text-red-400"><X className="w-4 h-4" /></button>
            </div>
          ))}
          <button type="button" onClick={() => setForm(f => ({ ...f, keys: [...f.keys, { n: "", title: "", text: "" }] }))} className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-[var(--org-accent)] font-bold"><Plus className="w-3.5 h-3.5" /> Add key</button>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Roles section title"><input value={form.roles_title} onChange={set("roles_title")} className={inputCls} data-testid="tiq-page-roles-title" /></Field>
        <Field label="Roles section subtitle"><input value={form.roles_sub} onChange={set("roles_sub")} className={inputCls} data-testid="tiq-page-roles-sub" /></Field>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Menu title (blank = 'Pick your role')"><input value={form.menu_title} onChange={set("menu_title")} className={inputCls} data-testid="tiq-page-menu-title" /></Field>
        <Field label="Menu subtitle"><input value={form.menu_sub} onChange={set("menu_sub")} className={inputCls} data-testid="tiq-page-menu-sub" /></Field>
      </div>
    </EditorShell>
  );
}

// ---------- Link videos from an IQ Library course ----------
export function LinkCourseDialog({ page, onClose, onLinked }) {
  const [courses, setCourses] = useState(null);
  const [courseId, setCourseId] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => { api.listIQCourses().then(d => setCourses(d.courses || [])).catch(() => setCourses([])); }, []);
  const run = async () => {
    if (!courseId) return toast.error("Pick a course");
    setRunning(true);
    try {
      const r = await api.tiqLinkCourse(page.id, courseId);
      setResult(r); onLinked();
      toast.success(`Linked ${r.linked} video${r.linked === 1 ? "" : "s"}`);
    } catch { toast.error("Linking failed"); } finally { setRunning(false); }
  };
  return (
    <EditorShell open onClose={onClose} eyebrow="Pull film from the IQ Library" title="Link course videos" onSave={run} saving={running} testId="tiq-link-course" saveLabel="Link videos">
      <p className="text-[13px] text-[#a0a0a0] leading-relaxed">Matches each option on <span className="text-white">{page.title}</span> that has no film yet to a video in the chosen IQ course by title (section titles help break ties). Options that already have film are left alone.</p>
      <Field label="IQ Library course">
        {courses === null ? <div className="text-[12px] text-[#757575]"><Loader2 className="w-3.5 h-3.5 animate-spin inline mr-2" />Loading courses…</div> : courses.length === 0 ? <div className="text-[12px] italic text-[#757575]">No courses in the IQ Library for this organization.</div> : (
          <select value={courseId} onChange={(e) => setCourseId(e.target.value)} className={inputCls} data-testid="tiq-link-course-select">
            <option value="">Choose a course…</option>
            {courses.map(c => <option key={c.id} value={c.id}>{c.title}{c.category ? ` · ${c.category}` : ""}</option>)}
          </select>
        )}
      </Field>
      {result && (
        <div className="border border-white/10 bg-[#101010]/60 p-4 text-[12px] space-y-1.5" data-testid="tiq-link-course-result">
          <div className="text-emerald-300">Linked {result.linked} option{result.linked === 1 ? "" : "s"} · {result.already_had_video} already had film · {result.course_videos} videos in course</div>
          {result.unmatched?.length > 0 && <div className="text-[#a0a0a0]">No match for: <span className="text-white">{result.unmatched.join(", ")}</span> — paste those links manually.</div>}
        </div>
      )}
    </EditorShell>
  );
}

// ---------- Import a whole IQ Library course as a new page ----------
export function ImportCourseDialog({ section, sectionTitle, onClose, onImported }) {
  const [courses, setCourses] = useState(null);
  const [courseId, setCourseId] = useState("");
  const [running, setRunning] = useState(false);
  useEffect(() => { api.listIQCourses().then(d => setCourses(d.courses || [])).catch(() => setCourses([])); }, []);
  const run = async () => {
    if (!courseId) return toast.error("Pick a course");
    setRunning(true);
    try {
      const p = await api.tiqImportCourse(courseId, section);
      toast.success(`Imported "${p.title}" — ${p.option_count} videos`);
      onImported(p);
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); } finally { setRunning(false); }
  };
  return (
    <EditorShell open onClose={onClose} eyebrow={`Import into ${sectionTitle}`} title="Import IQ course" onSave={run} saving={running} testId="tiq-import-course" saveLabel="Import">
      <p className="text-[13px] text-[#a0a0a0] leading-relaxed">Creates a new {sectionTitle} page from an IQ Library course: each chapter becomes a step, each video becomes a Film option. The course stays in the IQ Library untouched — you can reshape the page afterwards.</p>
      <Field label="IQ Library course">
        {courses === null ? <div className="text-[12px] text-[#757575]"><Loader2 className="w-3.5 h-3.5 animate-spin inline mr-2" />Loading courses…</div> : courses.length === 0 ? <div className="text-[12px] italic text-[#757575]">No courses in the IQ Library for this organization.</div> : (
          <select value={courseId} onChange={(e) => setCourseId(e.target.value)} className={inputCls} data-testid="tiq-import-course-select">
            <option value="">Choose a course…</option>
            {courses.map(c => <option key={c.id} value={c.id}>{c.title}{c.category ? ` · ${c.category}` : ""}</option>)}
          </select>
        )}
      </Field>
    </EditorShell>
  );
}
